import { getConfig } from '@sortlist-frontend/config';
import { TFunction, Trans } from '@sortlist-frontend/translation/ssr';

import { Slider } from '_components/common/Slider';

import { ReviewWidget } from './components/ReviewWidget/ReviewWidget';
import { SectionType } from './components/Section/Section';
import { TestimonialCard, TestimonialProps } from './components/TestimonialCard/TestimonialCard';

const { publicEnv } = getConfig();
const { s3Bucket: s3BucketUrl } = publicEnv;

export const getTestimonialsSection = (
  t: TFunction,
  testimonials: TestimonialProps[],
  clonedBlocksCount: number,
): SectionType => {
  return {
    name: 'testimonials',
    title: t('project:new.whatTheySay.title'),
    titleStyle: 'max-sm:px-4',
    fullWidthContent: true,
    content: (
      <Slider<TestimonialProps>
        className="mt-6"
        items={testimonials}
        Card={TestimonialCard}
        clonedBlocksCount={clonedBlocksCount}
      />
    ),
  };
};

export const getMainSections = (t: TFunction, onClick: () => void): SectionType[] => {
  return [
    {
      name: 'matching',
      title: t('project:new.matching.title'),
      description: t('project:new.matching.description'),
      content: <ReviewWidget />,
    },
    {
      name: 'trust',
      title: t('project:new.trust.title'),
      description: (
        <Trans i18nKey="project:new.trust.description">
          Data from both clients and providers means we know who’s really working with who.
          <br />
          <br />
          Who preps great meetings (and has client feedback to match). Who’s getting hired for this kind of project, and
          who’s delivering the goods.
          <br />
          <br />
          That's what enables the best match.
        </Trans>
      ),
      illustration: `${s3BucketUrl}/illustrations/project-new/trusted-match.webp`,
      imageAlt: t('project:new.trust.imageAlt'),
      width: 512,
      height: 460,
    },
    {
      name: 'workspace',
      title: t('project:new.workspace.title'),
      description: (
        <Trans i18nKey="project:new.workspace.description">
          Your workspace gives the professional structure of a "request for proposal" - for companies of all sizes.
          <br />
          <br />
          Decisions for all stakeholders are taken easily in a shared workspace: from checking portfolios, to tracking
          conversations, proposals, meetings, quotes, scorecards... even hiring.
        </Trans>
      ),
      inverted: true,
      illustration: `${s3BucketUrl}/illustrations/project-new/workspace-speed.webp`,
      imageAlt: t('project:new.workspace.imageAlt'),
      isCropped: true,
      width: 840,
      height: 770,
    },
    {
      name: 'free',
      title: (
        <Trans i18nKey="project:new.free.title">
          Yes, it's <span className="text-primary-500 italic">free</span> (and you're not the product)
        </Trans>
      ),
      description: (
        <Trans i18nKey="project:new.free.description">
          We select the most relevant providers. Those who are interested pay a fee to apply.
          <br />
          <br />
          That means they are incentivized to only pursue projects they can win, because they're a great match.
          <br />
          <br />
          So you get motivated experts who understand your project and are confident they can deliver.
        </Trans>
      ),
      illustration: `${s3BucketUrl}/illustrations/project-new/free-product.webp`,
      imageAlt: t('project:new.free.imageAlt'),
      width: 508,
      height: 500,
    },
    {
      name: 'post-project',
      title: (
        <Trans i18nKey="project:new.gainTime.title">
          Don't waste time searching.
          <br />
          <span className="text-primary-500 italic">Match</span> with your next provider.
        </Trans>
      ),
      description: t('project:new.gainTime.description'),
      cta: {
        label: (
          <Trans i18nKey="project:new.gainTime.ctaLabel">
            Post my project <span className="bold italic">- free</span>
          </Trans>
        ),
        onClick,
      },
    },
  ];
};

// @TODO: to be consolidated with clientQuotes in the future
// TBD if in public-api or still in public-ui
// See https://github.com/sortlist/sortlist/issues/25068
export const getTestimonials = (t: TFunction) => {
  return [
    {
      name: 'kelloggs',
      companyName: "Kellogg's",
      logo: `${s3BucketUrl}/logos/companies/kellogs-dark.svg`,
      author: 'Lauren H.',
      content: t('project:new.testimonials.kelloggs'),
    },
    {
      name: 'accenture',
      companyName: 'Accenture',
      logo: `${s3BucketUrl}/logos/companies/accenture-dark.svg`,
      author: 'Stephen M.',
      content: t('project:new.testimonials.accenture'),
    },
    {
      name: 'signify',
      companyName: 'Signify',
      logo: `${s3BucketUrl}/logos/companies/signify-dark.svg`,
      author: 'Catherine C.',
      content: t('project:new.testimonials.signify'),
    },
    {
      name: 'surfe',
      companyName: 'Surfe',
      logo: `${s3BucketUrl}/logos/companies/surfe-dark.svg`,
      author: 'Nina S.',
      content: t('project:new.testimonials.surfe'),
    },
    {
      name: 'loreal',
      companyName: "L'Oréal",
      logo: `${s3BucketUrl}/logos/companies/loreal-dark.svg`,
      author: 'Raphael P.',
      content: t('project:new.testimonials.loreal'),
    },
    {
      name: 'cws',
      companyName: 'CWS',
      logo: `${s3BucketUrl}/logos/companies/cws-dark.svg`,
      author: 'Julie N.',
      content: t('project:new.testimonials.cws'),
    },
  ];
};
